import {
  ArrowDownCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";
import CommonButton from "../common11/Buttons/CommonButton";
import CommonCheckbox from "../common11/formInputs/CommonCheckbox";
import CommonDatePicker from "../common11/formInputs/CommonDatePicker";
import InputAreaField from "../common11/formInputs/InputAreaField";
import InputField from "../common11/formInputs/InputField";
import RadioButtonGroup from "../common11/formInputs/RadioButtonGroup";
import SearchDropdown from "../common11/formInputs/SearchDropdown";
import CommonModal from "../common11/commonModals/CommonModal";
import { showInfoToast } from "../common11/toastUtils";

/** Wide, content-height modals – matches Case Paper Prescription / Services panels */
const CASE_PAPER_PANEL_MODAL_SX = {
  width: { xs: "calc(100vw - 16px)", sm: "92vw", lg: "88vw" },
  maxWidth: { xs: "calc(100vw - 16px)", sm: "1280px", lg: "1360px" },
  maxHeight: "fit-content",
};

/** Medium form dialogs – Add to Master etc. (attachment reference) */
const CASE_PAPER_FORM_MODAL_SX = {
  width: { xs: "calc(100vw - 32px)", sm: "min(520px, 92vw)" },
  maxWidth: { xs: "calc(100vw - 32px)", sm: "520px" },
  maxHeight: "fit-content",
};

function AddToMasterModal({ isOpen, onClose, label, value, onChange, onSave }) {
  const handleReset = () => onChange("");

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title={`Add to ${label} Master`}
      size="md"
      disableBackdropClose={false}
      disableScroll
      maxHeightVh={90}
      paperSx={CASE_PAPER_FORM_MODAL_SX}
      DialogContent={
        <div className="flex min-h-[10rem] flex-col justify-between gap-6 py-1">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">{label}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => onChange(e.target.value)}
              placeholder={`Enter new ${label.toLowerCase()}...`}
              className="w-full rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:border-[#217E90] focus:ring-1 focus:ring-[#217E90] focus:outline-none"
              onKeyDown={(e) => e.key === "Enter" && onSave()}
            />
          </div>
          <div className="flex justify-end gap-3 border-t border-gray-100 pt-4">
            <CommonButton
              label="Reset"
              variant="danger-outline"
              size="sm"
              type="button"
              className="flex-none px-4 text-xs md:text-sm"
              onClick={handleReset}
            />
            <CommonButton
              label="Save & Add"
              variant="primary-filled"
              size="sm"
              type="button"
              className="flex-none bg-[#217E90] px-4 text-xs hover:bg-[#1a6575] md:text-sm"
              onClick={onSave}
            />
          </div>
        </div>
      }
    />
  );
}

const CLINICAL_CARD_THEMES = {
  "Chief Complaints": {
    border: "border-t-red-400",
    btn: "bg-red-50 text-red-600 hover:bg-red-100 border border-red-100",
  },
  History: {
    border: "border-t-sky-400",
    btn: "bg-sky-50 text-sky-600 hover:bg-sky-100 border border-sky-100",
  },
  Allergy: {
    border: "border-t-amber-400",
    btn: "bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-100",
  },
  Examinations: {
    border: "border-t-purple-400",
    btn: "bg-purple-50 text-purple-600 hover:bg-purple-100 border border-purple-100",
  },
  Findings: {
    border: "border-t-green-400",
    btn: "bg-green-50 text-green-600 hover:bg-green-100 border border-green-100",
  },
  Diagnosis: {
    border: "border-t-indigo-500",
    btn: "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 border border-indigo-100",
  },
};

export function buildClinicalFields({
  chiefComplaintOptions,
  historyOptions,
  allergyOptions,
  examinationOptions,
  findingsOptions,
  diagnosisOptions,
}) {
  return [
    { name: "chiefComplaints", searchName: "chiefComplaintsSearch", label: "Chief Complaints", options: chiefComplaintOptions },
    { name: "history", searchName: "historySearch", label: "History", options: historyOptions },
    { name: "allergy", searchName: "allergySearch", label: "Allergy", options: allergyOptions },
    { name: "examinations", searchName: "examinationsSearch", label: "Examinations", options: examinationOptions },
    { name: "findings", searchName: "findingsSearch", label: "Findings", options: findingsOptions },
    { name: "diagnosis", searchName: "diagnosisSearch", label: "Diagnosis", options: diagnosisOptions },
  ];
}

export function ClinicalCardField({
  name,
  searchName,
  label,
  dataArray = [],
  control,
  setValue,
  watch,
}) {
  const theme = CLINICAL_CARD_THEMES[label] || {
    border: "border-t-gray-400",
    btn: "bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200",
  };
  const value = watch(name) ?? "";
  const items = value ? String(value).split("\n").filter(Boolean) : [];
  const [showAddToMasterModal, setShowAddToMasterModal] = useState(false);
  const [newMasterItem, setNewMasterItem] = useState("");

  const addItem = (text) => {
    const t = String(text).trim();
    if (!t) return;
    setValue(name, items.length ? `${value}\n${t}` : t, { shouldDirty: true });
    setValue(searchName, null, { shouldDirty: true });
  };

  const handleSelect = (selected, option) => {
    const text = option?.label ?? selected?.label ?? "";
    if (!String(text).trim()) return;
    addItem(text);
  };

  const handleCreateOption = (searchTerm) => {
    addItem(searchTerm);
  };

  const handleRemove = (index) => {
    const next = items.filter((_, i) => i !== index).join("\n");
    setValue(name, next, { shouldDirty: true });
  };

  const handleOpenAddToMaster = () => {
    setNewMasterItem("");
    setShowAddToMasterModal(true);
  };

  const handleSaveToMaster = () => {
    const t = String(newMasterItem).trim();
    if (!t) {
      showInfoToast("Please enter a value");
      return;
    }
    addItem(t);
    showInfoToast(`Added to ${label} master`);
    setShowAddToMasterModal(false);
    setNewMasterItem("");
  };

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden flex flex-col min-h-[7.5rem] border-t-4 ${theme.border}`}
    >
      <div className="flex items-start gap-1.5 px-2 pt-2 pb-1">
        <div className="flex-1 min-w-0">
          <SearchDropdown
            name={searchName}
            control={control}
            label={label}
            dataArray={dataArray}
            isSearchable
            isClearable
            isFloatingLabel
            fontSize="text-xs"
            selectSize="h-8"
            onChange={handleSelect}
            onCreateOption={handleCreateOption}
          />
        </div>
        <button
          type="button"
          onClick={handleOpenAddToMaster}
          className={`mt-0.5 h-8 w-8 shrink-0 rounded flex items-center justify-center transition-colors ${theme.btn}`}
          title={`Add to ${label} master`}
          aria-label={`Add to ${label} master`}
        >
          <DocumentTextIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="mx-2 mb-2 flex-1 flex flex-col min-h-0 rounded border border-gray-100 bg-gray-50/80 p-2">
        <div className="flex-1 flex flex-wrap items-start content-start gap-1 min-h-[4.5rem]">
          {items.length === 0 ? (
            <span className="text-[11px] text-gray-400 self-center w-full text-center py-2">
              No items added yet
            </span>
          ) : (
            items.map((item, i) => (
              <span
                key={i}
                className="inline-flex max-w-full items-center rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700"
              >
                <span className="truncate max-w-[140px]">{item}</span>
                <button
                  type="button"
                  onClick={() => handleRemove(i)}
                  className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-gray-100"
                  aria-label="Remove"
                >
                  <XMarkIcon className="h-2.5 w-2.5 text-slate-500" />
                </button>
              </span>
            ))
          )}
        </div>
      </div>

      <AddToMasterModal
        isOpen={showAddToMasterModal}
        onClose={() => setShowAddToMasterModal(false)}
        label={label}
        value={newMasterItem}
        onChange={setNewMasterItem}
        onSave={handleSaveToMaster}
      />
    </div>
  );
}

export function CasePaperGenericMainSection({
  control,
  watch,
  setValue,
  clinicalFields,
  prescriptionFields,
  investServiceFields,
  setShowPrescriptionModal,
  setShowServicesModal,
  followUpInstructionOptions,
}) {
  return (
    <div className="space-y-3 px-1 pb-2">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {clinicalFields.map((field) => (
          <ClinicalCardField
            key={field.name}
            name={field.name}
            searchName={field.searchName}
            label={field.label}
            dataArray={field.options}
            control={control}
            setValue={setValue}
            watch={watch}
          />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="flex items-center justify-between rounded-lg border border-orange-200 bg-orange-50/50 px-3 py-2.5">
          <span className="text-xs font-medium text-gray-700">
            {prescriptionFields.length} medicine
            {prescriptionFields.length === 1 ? "" : "s"} added
          </span>
          <button
            type="button"
            onClick={() => setShowPrescriptionModal(true)}
            className="rounded-md bg-orange-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-orange-600"
          >
            Add Prescription
          </button>
        </div>
        <div className="flex items-center justify-between rounded-lg border border-teal-200 bg-teal-50/50 px-3 py-2.5">
          <span className="text-xs font-medium text-gray-700">
            {investServiceFields.length} service
            {investServiceFields.length === 1 ? "" : "s"} added
          </span>
          <button
            type="button"
            onClick={() => setShowServicesModal(true)}
            className="rounded-md bg-[#217E90] px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-[#1a6575]"
          >
            Add Services
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-2 min-h-[8rem]">
          <InputAreaField
            name="myThought"
            control={control}
            label="My Thought"
            minRows={4}
            fontSize="text-xs"
            enableSpeechToText
            isFloatingLabel
            className="w-full min-h-[6rem]"
          />
        </div>
        <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-2 min-h-[8rem]">
          <CommonCheckbox
            name="followUp"
            control={control}
            label="Follow up"
            fontSize="text-xs"
            checkboxSize="h-4 w-4"
            checkboxColor="bg-[#217E90]"
            borderColor="border-gray-300"
            textColor="text-gray-700"
          />
          <div className="grid grid-cols-2 gap-2 items-end min-w-0">
            <div className="min-w-0">
              <CommonDatePicker
                name="followUpDate"
                control={control}
                label="Followup Date"
                fontSize="text-xs"
                disablePast
              />
            </div>
            <div className="min-w-0">
              <SearchDropdown
                name="followUpInstructionMulti"
                control={control}
                label="Followup Instructions"
                dataArray={followUpInstructionOptions}
                isSearchable
                isClearable
                isMulti
                fontSize="text-xs"
                selectSize="h-9"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CasePaperPrescriptionModal({
  isOpen,
  onClose,
  control,
  watch,
  setFocus,
  prescriptionFields,
  removePrescriptionRow,
  showPrescriptionHistory,
  setShowPrescriptionHistory,
  mockPreviousPrescriptions,
  setSelectedPrescriptionForView,
  handlePatchPrescription,
  isDuplicateDrug,
  drugOptions,
  handleDrugSelect,
  onDrugSearch,
  drugsLoading = false,
  doseOptions,
  onDoseSearch,
  dosesLoading = false,
  durInOptions,
  drugSearchWrapperRef,
  drugDoseWrapperRef,
  drugDurationWrapperRef,
  drugDurInWrapperRef,
  drugTotalWrapperRef,
  drugAddButtonRef,
  prescriptionTableScrollRef,
  focusDrugDose,
  focusDrugDuration,
  focusDrugDurIn,
  focusDrugTotal,
  focusDrugAddButton,
  focusDrugSearch,
  handleAddDrug,
}) {
  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Prescription"
      size="xl"
      disableBackdropClose={false}
      disableScroll
      maxHeightVh={90}
      paperSx={CASE_PAPER_PANEL_MODAL_SX}
      DialogContent={
        <div className="rounded-lg border border-slate-300 bg-white p-3 space-y-3 relative -my-1">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex-1 min-w-0 pr-2">
              <RadioButtonGroup
                name="prescriptionMode"
                control={control}
                defaultValue="prescNew"
                direction="horizontal"
                optionLabelClassName="text-[11px]"
                options={[
                  { label: "New", value: "prescNew" },
                  { label: "Same As Prev.", value: "sameAsPrev" },
                  { label: "Use Generic", value: "useGeneric" },
                  { label: "Readymade Pre.", value: "readymadePre" },
                  { label: "Move To Template", value: "moveToTemplate" },
                  { label: "Last IPD Prescription", value: "lastIpdPrescription" },
                ]}
              />
            </div>
            <button
              type="button"
              onClick={() => setShowPrescriptionHistory(true)}
              className="flex h-8 w-8 items-center justify-center rounded border-2 border-orange-400 text-orange-500 hover:bg-orange-50 flex-shrink-0"
              title="Prescription history"
              aria-label="Prescription history"
            >
              <ClockIcon className="h-4 w-4" />
            </button>
          </div>

          {showPrescriptionHistory && (
            <>
              <div
                className="fixed inset-0 z-[998] bg-black/40"
                onClick={() => setShowPrescriptionHistory(false)}
              />
              <div
                className="absolute right-3 top-10 z-[999] w-[520px] max-w-[95vw] rounded-lg border border-gray-200 shadow-xl bg-[#F8FEFF]"
              >
                <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 bg-[#F0F9FF] rounded-t-lg">
                  <span className="text-xs font-semibold text-[#217E90]">Previous Prescriptions</span>
                  <button
                    type="button"
                    onClick={() => setShowPrescriptionHistory(false)}
                    className="flex h-6 w-6 items-center justify-center rounded-full border border-red-200 text-red-500 hover:bg-red-50"
                    aria-label="Close history"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="p-3 max-h-[18rem] overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0 z-[1]">
                      <tr>
                        <th className="border-b px-2 py-1.5 text-center font-medium text-gray-600 w-24">Action</th>
                        <th className="border-b px-2 py-1.5 text-left font-medium text-gray-600">Date</th>
                        <th className="border-b px-2 py-1.5 text-left font-medium text-gray-600">Doctor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockPreviousPrescriptions.map((presc) => (
                        <tr key={presc.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                          <td className="px-2 py-1">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                type="button"
                                onClick={() => setSelectedPrescriptionForView(presc)}
                                className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] bg-white"
                                title="View medicines"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePatchPrescription(presc)}
                                className="inline-flex items-center justify-center h-7 w-7 rounded-full border border-[#217E90] text-[#217E90] hover:bg-[#E9F5FE] bg-white"
                                title="Add all medicines to current prescription"
                              >
                                <ArrowDownCircleIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                          <td className="px-2 py-1.5">{presc.date}</td>
                          <td className="px-2 py-1.5">{presc.doctorName}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          <div className="flex flex-wrap items-end gap-2">
            <div ref={drugSearchWrapperRef} className="flex-[1.1] min-w-[9rem] max-w-[14rem]">
              <SearchDropdown
                name="drugSearch"
                control={control}
                label="Drug Name"
                dataArray={drugOptions}
                onSearch={onDrugSearch}
                isLoading={drugsLoading}
                isSearchable
                isClearable
                menuPortal
                fontSize="text-xs"
                selectSize="h-8"
                inputBgColor={isDuplicateDrug ? "bg-yellow-50" : "bg-gray-50"}
                onChange={handleDrugSelect}
                onEnterWhenClosed={isDuplicateDrug ? undefined : () => setFocus("drugGenericName")}
              />
            </div>
            <div className="flex-[1.2] min-w-[9rem] max-w-[14rem]">
              <InputField
                name="drugGenericName"
                control={control}
                label="Generic Name"
                isFloatingLabel
                fontSize="text-xs"
                inputSize="h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    focusDrugDose();
                  }
                }}
              />
            </div>
            <div ref={drugDoseWrapperRef} className="w-[6.5rem] shrink-0">
              <SearchDropdown
                name="drugDose"
                control={control}
                label="Dose"
                dataArray={doseOptions}
                onSearch={onDoseSearch}
                isLoading={dosesLoading}
                isSearchable
                isClearable
                menuPortal
                fontSize="text-xs"
                selectSize="h-8"
                onChange={focusDrugDuration}
                onEnterWhenClosed={focusDrugDuration}
              />
            </div>
            <div ref={drugDurationWrapperRef} className="w-[5.5rem] shrink-0">
              <InputField
                name="drugDuration"
                control={control}
                label="Duration"
                isFloatingLabel
                fontSize="text-xs"
                inputSize="h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    focusDrugDurIn();
                  }
                }}
              />
            </div>
            <div ref={drugDurInWrapperRef} className="w-[5.5rem] shrink-0">
              <SearchDropdown
                name="drugDurIn"
                control={control}
                label="Dur.In"
                dataArray={durInOptions}
                isSearchable={false}
                isClearable
                fontSize="text-xs"
                selectSize="h-8"
                onChange={focusDrugTotal}
                onEnterWhenClosed={focusDrugTotal}
              />
            </div>
            <div ref={drugTotalWrapperRef} className="w-[5.5rem] shrink-0">
              <InputField
                name="drugTotal"
                control={control}
                label="Total Qty"
                isFloatingLabel
                fontSize="text-xs"
                inputSize="h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setFocus("drugInstruction");
                  }
                }}
              />
            </div>
            <div className="flex-[1] min-w-[7rem] max-w-[12rem]">
              <InputField
                name="drugInstruction"
                control={control}
                label="Instruction"
                isFloatingLabel
                fontSize="text-xs"
                inputSize="h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setFocus("drugRemark");
                  }
                }}
              />
            </div>
            <div className="flex-[1] min-w-[7rem] max-w-[12rem]">
              <InputField
                name="drugRemark"
                control={control}
                label="Remark"
                isFloatingLabel
                fontSize="text-xs"
                inputSize="h-8"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    focusDrugAddButton();
                  }
                }}
              />
            </div>
            <button
              type="button"
              ref={drugAddButtonRef}
              onClick={() => {
                handleAddDrug();
                focusDrugSearch();
              }}
              className="mb-0.5 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded bg-orange-500 text-white hover:bg-orange-600"
              title="Add medicine"
              aria-label="Add medicine"
            >
              <PlusIcon className="h-5 w-5" />
            </button>
          </div>
          <div
            ref={prescriptionTableScrollRef}
            className={`overflow-x-auto rounded border border-gray-200 ${
              prescriptionFields.length > 4 ? "max-h-[11rem] overflow-y-auto" : "min-h-[10rem]"
            }`}
          >
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-[1]">
                  <tr>
                    <th className="border-b px-2 py-1 text-center font-medium text-gray-600 w-16">Action</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Drug Name</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Generic Name</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Dose</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Duration</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Dur.In</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Total Qty</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Instruction</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Remark</th>
                  </tr>
                </thead>
                <tbody>
                  {prescriptionFields.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-2 py-8 text-center text-gray-400 align-middle">
                        No medicines added
                      </td>
                    </tr>
                  ) : (
                    prescriptionFields.map((row, i) => (
                      <tr key={row.id}>
                        <td className="border-b border-gray-100 px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => removePrescriptionRow(i)}
                            className="inline-flex items-center justify-center text-red-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.drugName ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.genericName ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.dose ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.duration ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.durIn ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.totalQty ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.instruction ?? ""}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.remark ?? ""}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
        </div>
      }
    />
  );
}

export function CasePaperServicesModal({
  isOpen,
  onClose,
  control,
  watch,
  investServiceFields,
  removeInvestService,
  handleInvestTemplateToggle,
  handleAddInvestService,
  investigationTemplateOptions,
  investigationServiceOptions,
  onServiceSearch,
  servicesLoading = false,
  investigationTableScrollRef,
}) {
  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title="Services"
      size="xl"
      disableBackdropClose={false}
      disableScroll
      maxHeightVh={90}
      paperSx={CASE_PAPER_PANEL_MODAL_SX}
      DialogContent={
        <div className="rounded-lg border border-slate-300 bg-white p-3 space-y-3 -my-1">
          <div className="flex flex-wrap items-end gap-2">
            <label className="inline-flex items-center gap-1.5 cursor-pointer select-none flex-shrink-0 pb-1">
              <input
                type="checkbox"
                checked={!!watch("investTemplate")}
                onChange={handleInvestTemplateToggle}
                className="h-4 w-4 rounded border-gray-300 text-[#217E90] focus:ring-[#217E90]"
              />
              <span className="text-xs font-medium text-gray-700">Template</span>
            </label>
            <div className="flex flex-1 min-w-[12rem] items-end gap-2">
              <div className="flex-1 min-w-0">
                <SearchDropdown
                  name="investServiceSearch"
                  control={control}
                  label={watch("investTemplate") ? "Search template" : "Search by Service Name"}
                  dataArray={
                    watch("investTemplate")
                      ? investigationTemplateOptions
                      : investigationServiceOptions
                  }
                  onSearch={watch("investTemplate") ? undefined : onServiceSearch}
                  isLoading={!watch("investTemplate") && servicesLoading}
                  isSearchable
                  isClearable
                  menuPortal
                  fontSize="text-xs"
                  selectSize="h-8"
                />
              </div>
              <button
                type="button"
                onClick={handleAddInvestService}
                className="mb-0.5 h-8 w-8 shrink-0 inline-flex items-center justify-center rounded bg-orange-500 text-white hover:bg-orange-600"
                title="Add service"
                aria-label="Add service"
              >
                <PlusIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="rounded border border-gray-200 overflow-hidden min-h-[12rem]">
            <div
              ref={investigationTableScrollRef}
              className={`overflow-y-auto overflow-x-auto ${
                investServiceFields.length > 5 ? "max-h-[14rem]" : "min-h-[11rem]"
              }`}
            >
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-[1]">
                  <tr>
                    <th className="border-b px-2 py-1 text-center font-medium text-gray-600 w-16">Action</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Code</th>
                    <th className="border-b px-2 py-1 text-left font-medium text-gray-600">Service Name</th>
                  </tr>
                </thead>
                <tbody>
                  {investServiceFields.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-2 py-8 text-center text-gray-400 align-middle">
                        No services added
                      </td>
                    </tr>
                  ) : (
                    investServiceFields.map((row, i) => (
                      <tr key={row.id}>
                        <td className="border-b border-gray-100 px-2 py-1 text-center">
                          <button
                            type="button"
                            onClick={() => removeInvestService(i)}
                            className="inline-flex items-center justify-center text-red-500 hover:text-red-600"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.code}</td>
                        <td className="border-b border-gray-100 px-2 py-1">{row.serviceName}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      }
    />
  );
}
